/**
 * SpecCostsPanel Component
 *
 * Displays cost tracking data for a specification within SpecViewer tabs.
 * Shows execution costs, model breakdown, and cost summary.
 *
 * Part of PowerSpec V3 - CPO Dashboard Integration
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Badge, Spinner, Alert, Select } from 'flowbite-react';
import {
  HiCurrencyDollar,
  HiChartPie,
  HiTrendingUp,
  HiTrendingDown,
  HiMinus,
  HiRefresh
} from 'react-icons/hi';
import {
  getSpecCosts,
  getSpecCostSummary,
  formatCost,
  type SpecCost,
  type SpecCostSummary,
  type ModelDistribution
} from '../api/specifications';
import { isSuccessResponse } from '../api/apiClient';

interface SpecCostsPanelProps {
  specificationId: string;
  onError?: (error: string) => void;
}

type TimePeriod = 'this_month' | 'last_month' | 'last_quarter' | 'all_time';

export const SpecCostsPanel: React.FC<SpecCostsPanelProps> = ({
  specificationId,
  onError
}) => {
  const [costs, setCosts] = useState<SpecCost[]>([]);
  const [summary, setSummary] = useState<SpecCostSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<TimePeriod>('this_month');

  const getDateRange = useCallback((timePeriod: TimePeriod) => {
    const now = new Date();
    let start: Date;

    switch (timePeriod) {
      case 'this_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        break;
      case 'last_quarter':
        const currentQuarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
        break;
      case 'all_time':
      default:
        return {};
    }

    return {
      period_start: start.toISOString().split('T')[0],
      period_end: now.toISOString().split('T')[0]
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange(period);

      // Fetch costs and summary in parallel
      const [costsResponse, summaryResponse] = await Promise.all([
        getSpecCosts(specificationId, { ...dateRange, limit: 50 }),
        getSpecCostSummary(specificationId, { ...dateRange, include_breakdown: true })
      ]);

      if (isSuccessResponse(costsResponse)) {
        setCosts(costsResponse.data?.Records || []);
      }

      if (isSuccessResponse(summaryResponse)) {
        const summaryData = summaryResponse.data?.Records?.[0];
        setSummary(summaryData || null);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load cost data';
      console.error('[SpecCostsPanel] Error:', err);
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [specificationId, period, getDateRange, onError]);

  useEffect(() => {
    if (specificationId) {
      fetchData();
    }
  }, [specificationId, fetchData]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getModelBadgeColor = (modelType: string) => {
    switch (modelType) {
      case 'opus':
        return 'purple';
      case 'sonnet':
        return 'blue';
      case 'haiku':
        return 'green';
      default:
        return 'gray';
    }
  };

  const TrendIcon = summary?.Trend?.Direction === 'up' ? HiTrendingUp :
                    summary?.Trend?.Direction === 'down' ? HiTrendingDown : HiMinus;

  const trendColorClass = summary?.Trend?.Direction === 'up' ? 'text-red-500' :
                          summary?.Trend?.Direction === 'down' ? 'text-green-500' : 'text-gray-500';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure">
        <div>
          <p className="font-semibold">Error loading cost data</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            className="mt-2 text-sm underline"
            onClick={fetchData}
          >
            Retry
          </button>
        </div>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <HiCurrencyDollar className="w-5 h-5 text-green-600" />
          Execution Costs
        </h3>
        <div className="flex items-center gap-2">
          <Select
            sizing="sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value as TimePeriod)}
          >
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_quarter">Last Quarter</option>
            <option value="all_time">All Time</option>
          </Select>
          <button
            onClick={fetchData}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <HiRefresh className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary Card */}
      {summary && (
        <Card className="border-l-4 border-blue-500 bg-blue-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Total Cost */}
            <div>
              <div className="text-sm text-gray-600">Total Cost</div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-gray-900">
                  {formatCost(summary.Total_Cost)}
                </span>
                {summary.Trend && (
                  <span className={`flex items-center text-sm ${trendColorClass}`}>
                    <TrendIcon className="w-4 h-4" />
                    {Math.abs(summary.Trend.Change_Percentage).toFixed(1)}%
                  </span>
                )}
              </div>
            </div>

            {/* Executions */}
            <div>
              <div className="text-sm text-gray-600">Executions</div>
              <div className="text-2xl font-bold text-gray-900">
                {summary.Total_Executions.toLocaleString()}
              </div>
            </div>

            {/* Avg Per Execution */}
            <div>
              <div className="text-sm text-gray-600">Avg per Execution</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCost(summary.Avg_Cost_Per_Execution)}
              </div>
            </div>

            {/* Period */}
            <div>
              <div className="text-sm text-gray-600">Period</div>
              <div className="text-sm font-medium text-gray-900">
                {new Date(summary.Period_Start).toLocaleDateString()} -
                {new Date(summary.Period_End).toLocaleDateString()}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Model Distribution */}
      {summary?.Model_Distribution && summary.Model_Distribution.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <HiChartPie className="w-5 h-5 text-gray-400" />
            <h4 className="font-semibold text-gray-900">Model Distribution</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {summary.Model_Distribution.map((dist: ModelDistribution) => (
              <div
                key={dist.model}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge color={getModelBadgeColor(dist.model_type)}>
                    {dist.model}
                  </Badge>
                  <span className="text-sm font-medium text-gray-600">
                    {dist.percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Cost</span>
                    <span className="font-medium">{formatCost(dist.total_cost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Executions</span>
                    <span className="font-medium">{dist.execution_count.toLocaleString()}</span>
                  </div>
                </div>
                {/* Mini progress bar */}
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${
                      dist.model_type === 'opus' ? 'bg-purple-500' :
                      dist.model_type === 'sonnet' ? 'bg-blue-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${dist.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Cost History Table */}
      <Card>
        <h4 className="font-semibold text-gray-900 mb-4">Recent Executions</h4>
        {costs.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No execution costs recorded for this period
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>Date</Table.HeadCell>
                <Table.HeadCell>Model</Table.HeadCell>
                <Table.HeadCell>Operation</Table.HeadCell>
                <Table.HeadCell>Tokens</Table.HeadCell>
                <Table.HeadCell>Cost</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {costs.map((cost) => (
                  <Table.Row key={cost.Spec_Cost_ID} className="bg-white">
                    <Table.Cell className="whitespace-nowrap text-sm text-gray-600">
                      {formatDate(cost.Create_Date)}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={getModelBadgeColor(cost.Model_Type)}>
                        {cost.Model_Name}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="text-sm text-gray-600">
                      {cost.Operation_Type}
                    </Table.Cell>
                    <Table.Cell className="text-sm">
                      <span className="text-gray-500">In: </span>
                      <span className="font-mono">{cost.Input_Tokens.toLocaleString()}</span>
                      <span className="mx-1 text-gray-400">|</span>
                      <span className="text-gray-500">Out: </span>
                      <span className="font-mono">{cost.Output_Tokens.toLocaleString()}</span>
                    </Table.Cell>
                    <Table.Cell className="font-medium text-gray-900">
                      {formatCost(cost.Total_Cost)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SpecCostsPanel;
